<template>
  <main class="email-container" :class="[displayLayout, isNewClass]">
    <p class="popup-title">
      <van-icon name="arrow-left" @click="back" />
      {{ popupTitle }}
    </p>
    <section class="email-content">
      <template v-if="displayEmailInput">
        <div>
          <!-- 1.请输入您的邮箱号-->
          <span class="title">{{ $t('account.input_email') }}</span>
          <div class="item-input">
            <van-field
              v-model="email"
              @blur="handleBlur"
              @input="handleInput"
              clearable
              :readonly="false"
            />
          </div>
          <p
            class="error-message"
            v-if="allowShowErrTip && email && !isValidEmail"
          >
            <MBIcon
              :size="isMobile ? 36 : 12"
              name="icon-warn"
              color="#FF0000"
              class="icon"
            />
            {{ $t('account.email_format_error') }}
          </p>
          <security-tip :tip="$t('account.account_security_tip')" />
        </div>
        <div class="email-submit">
          <button
            :class="!isValidEmail ? 'disabled' : ''"
            @click="next"
            :disabled="!isValidEmail"
          >
            {{ $t('account.confirm') }}
          </button>
        </div>
      </template>
      <template v-else>
        <div>
          <!-- 2.输入邮箱验证码 -->
          <span class="title">{{ $t('account.input_verification_code') }}</span>
          <password-base
            class="email-code"
            v-model="code"
            gutter="5px"
            :showKeyboard="showKeyBoard"
            :numVisible="true"
          />
        </div>
        <security-tip :tip="emailTip" />
        <div class="email-submit">
          <button
            :class="!valid ? 'disabled' : ''"
            @click="confirm"
            :disabled="!valid"
          >
            {{ $t('account.confirm') }}
          </button>
        </div>
      </template>
    </section>
  </main>
</template>

<script>
  import { mapState, mapGetters } from 'vuex'
  import { emailReg, sixDigitsReg } from '@/utils/regExp'
  import { delCookie } from '@/utils/login'
  import { sendCode } from '@/api/coin'
  import PasswordBase from '@/components/PasswordBase'
  import SecurityTip from '../SecurityTip/index.vue'
  import MBIcon from '@/components/MBIcon'
  import { Toast } from 'vant'
  import debounce from 'lodash/debounce'

  export default {
    name: 'email-verification',
    components: {
      PasswordBase,
      SecurityTip,
      MBIcon,
    },
    props: {
      configData: {
        type: Object,
        default: () => {},
      },
      pwOperation: {
        type: String,
        default: 'initPW',
      },
    },
    data() {
      return {
        email: '',
        code: '',
        codeId: '',
        isValidEmail: false,
        valid: false,
        allowShowErrTip: false,
        isBefore: true,
        showKeyBoard: true,
        checkFormat: null,
      }
    },
    computed: {
      ...mapState(['userInfo', 'isMobile']),
      ...mapGetters(['displayLayout', 'isNewClass']),
      walletBoundEmail() {
        return this.configData?.email || ''
      },
      popupTitle() {
        return this.isBefore
          ? this.$t('account.email_binding')
          : this.$t('account.verification_code_title')
      },
      displayEmailInput() {
        return this.isBefore && this.pwOperation === 'initPW'
      },
      emailTip() {
        const text = this.$t('account.authentication_tip')
        return text.replace('{email_address}', this.walletBoundEmail)
      },
    },
    watch: {
      code(val) {
        this.codeValid(val)
      },
      email() {
        this.checkFormat()
      },
    },
    created() {
      if (this.walletBoundEmail) {
        // 如果存在邮箱则跳过绑定邮箱的步骤
        this.isBefore = false
        this.email = this.walletBoundEmail
        this.next()
      }
    },
    mounted() {
      this.checkFormat = debounce(this.emailValid, 800)
    },
    destroyed() {
      delCookie('pw')
    },
    methods: {
      handleUserinfoNotObtained() {
        Toast.fail(this.$t('common.login_again'))
      },
      handleBlur() {
        this.emailValid()
      },
      handleInput() {
        // 输入字符期间不要显示错误提示
        this.allowShowErrTip = false
      },
      emailValid() {
        const { email } = this
        // 校验邮箱格式
        if (emailReg.test(email)) {
          this.isValidEmail = true
        } else {
          this.isValidEmail = false
          this.allowShowErrTip = true
        }
      },
      codeValid(code) {
        // 验证码校验
        if (sixDigitsReg.test(code)) {
          this.valid = true
          this.showKeyBoard = false
        } else {
          this.valid = false
        }
      },
      back() {
        if (!this.displayEmailInput) {
          // 当前在第一步，点击返回至上一页
          this.$emit('jumpto', 'password-setting')
        } else {
          // 当前在第二步，点击标题返回上一步
          this.isBefore = true
          this.emailValid()
          return
        }
      },
      async next() {
        // 发送校验码
        let sceneCode = this.pwOperation === 'initPW' ? 1 : 2
        const params = {
          userId: this.userInfo.kicoinId, // 非必填
          effectiveTime: 24 * 3600,
          sceneCode, // 业务场景类型 1.钱包绑定验证 2.钱包修改密码
          touchAddress: this.email,
          touchType: 1,
        }
        this.codeId = ''
        try {
          const response = await sendCode(params)
          if (response.codeId) {
            this.codeId = response.codeId
          }
          this.isBefore = false
        } catch (e) {
          const { code } = e
          if (String(code) === '10003') {
            this.handleUserinfoNotObtained()
          }
          this.$emit('jumpto', {
            type: 'result',
            data: {
              status: 0,
              errcode: code,
            },
          })
        }
      },
      async confirm() {
        const { email, code, codeId } = this
        // 直接去请求绑定密码的接口
        this.$emit('jumpto', { type: 'request', data: { email, code, codeId } })
      },
    },
  }
</script>

<style lang="scss" scoped src="./index.scss"></style>
<style lang="scss" scoped>
  @media screen and (min-device-width: 1080px) {
    @import './index_pc.scss';
  }
</style>
<style lang="scss" scoped>
  .van-cell {
    padding: 0;
  }
  input::placeholder {
    color: rgba(0, 0, 0, 0.4);
  }
  .van-field__control {
    color: #000000;
  }

  .van-toast--bottom {
    background: #ffffff;
    box-shadow: 0px 8px 26px rgba(0, 0, 0, 0.1);
    border-radius: 36px;
    font-family: Manrope;
    font-weight: 600;
    font-size: 36px;
    line-height: 49px;
    text-align: center;
    color: #000000;
  }
</style>
